import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/**
 * Renders `element` to a PDF that matches on-screen layout (colors, typography from computed styles).
 * Uses landscape A4. Tall content is split by slicing the canvas per page (avoids black gaps from tiled full images).
 */
export async function downloadElementAsPdf(element: HTMLElement, fileBaseName: string): Promise<void> {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
    // Do not set windowWidth/windowHeight — wrong values cause horizontal bands / clipped tiles in the capture.
  });

  const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const imgWidthMm = pageWidth;
  const imgHeightMm = (canvas.height * imgWidthMm) / canvas.width;

  const sliceCanvas = (src: HTMLCanvasElement, srcY: number, sliceH: number): HTMLCanvasElement => {
    const h = Math.min(Math.max(1, Math.floor(sliceH)), src.height - srcY);
    const out = document.createElement("canvas");
    out.width = src.width;
    out.height = h;
    const ctx = out.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas unsupported");
    }
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(src, 0, srcY, src.width, h, 0, 0, src.width, h);
    return out;
  };

  if (imgHeightMm <= pageHeight + 0.05) {
    const imgData = canvas.toDataURL("image/png");
    pdf.addImage(imgData, "PNG", 0, 0, imgWidthMm, imgHeightMm);
  } else {
    const pageHeightPx = Math.floor((pageHeight / imgHeightMm) * canvas.height);
    let srcY = 0;
    let page = 0;

    while (srcY < canvas.height) {
      if (page > 0) {
        pdf.addPage();
      }
      const remainingPx = canvas.height - srcY;
      const sliceH = Math.min(pageHeightPx, remainingPx);
      const slice = sliceCanvas(canvas, srcY, sliceH);
      const sliceMm = (slice.height * imgWidthMm) / canvas.width;
      pdf.addImage(slice.toDataURL("image/png"), "PNG", 0, 0, imgWidthMm, sliceMm);
      srcY += slice.height;
      page += 1;
    }
  }

  const safeName =
    fileBaseName.replace(/[^a-zA-Z0-9-_]+/g, "-").replace(/^-|-$/g, "") || "express-interest-preview";
  pdf.save(`${safeName}.pdf`);
}
